import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { message } from "antd";
import { getApi } from "../../../../../api/api_instance";
import { API_URLS } from "../../../../../api/apiUrls";
import FormQuestionInput, { RequiredMark } from "./QuestionInput";
import PopupCard from "../../../../Auth/PopupCard";
import {
  buildFieldTree,
  computeFieldNumbersForContext,
  formatSerialDisplay,
} from "./serialNumberUtils";

const FieldSerialPrefix = ({ fieldNumbers, fieldId }) => {
  const display = formatSerialDisplay(fieldNumbers?.get(fieldId));
  if (!display) return null;
  return <span className="text-teal-700 mr-2">{display}</span>;
};

const tableDefinitionCache = new Map();

export const useTableDefinition = (tableRowTypeId) => {
  const [definition, setDefinition] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tableRowTypeId || typeof tableRowTypeId !== "string") {
      return;
    }

    if (tableDefinitionCache.has(tableRowTypeId)) {
      setDefinition(tableDefinitionCache.get(tableRowTypeId) || null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const res = await getApi({
          apiUrl: API_URLS.GET_TABLE_ROW_TYPE_DEFINITION(tableRowTypeId),
        });
        if (!cancelled && res && res.success) {
          tableDefinitionCache.set(tableRowTypeId, res.data);
          setDefinition(res.data);
        }
      } catch (err) {
        console.error(`Failed table definition load ${tableRowTypeId}`, err);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [tableRowTypeId]);

  return { definition, loading };
};

// --- MAIN COMPONENT ---

export const TableManager = ({
  question,
  tableId,
  answers,
  setAnswers,
  evalCondition,
  tripIndex,
  onLoadDefinition,
  parentMandatory = false,
  parentQuestionNumber = "",
  readOnly = false,
}) => {
  const { definition, loading } = useTableDefinition(question.tableRowTypeId);
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState({
    type: "error",
    title: "Validation Error",
    message: "",
  });

  useEffect(() => {
    if (!showPopup) return;
    const timer = setTimeout(() => setShowPopup(false), 3000);
    return () => clearTimeout(timer);
  }, [showPopup]);

  useEffect(() => {
    if (definition && onLoadDefinition) {
      onLoadDefinition(definition);
    }
  }, [definition, onLoadDefinition, question.questionId]);

  const fields = definition?.fields || [];

  const fieldTree = useMemo(() => buildFieldTree(fields), [fields]);

  const categoryFields = useMemo(
    () =>
      fields.filter(
        (f) =>
          ["multiselect", "multi-select"].includes(f.fieldType) &&
          (!f.displayCondition || f.displayCondition.length === 0) &&
          fields.some((other) =>
            other.displayCondition?.some(
              (condition) =>
                (condition.triggerQuestionId || condition.triggerFieldId) ===
                f.fieldId,
            ),
          ),
      ),
    [fields],
  );

  const isCategorized = categoryFields.length > 0;

  const categoryFieldIds = useMemo(
    () => new Set(categoryFields.map((field) => field.fieldId)),
    [categoryFields],
  );

  const belongsToField = (rowKey, fieldId) => {
    if (rowKey === fieldId) return true;
    if (rowKey.startsWith(`${fieldId}_`)) {
      return !fields.some(
        (f) =>
          f.fieldId !== fieldId &&
          f.fieldId.length > fieldId.length &&
          rowKey.startsWith(f.fieldId),
      );
    }
    return false;
  };

  const getFieldNumbersForRow = (rowContext) =>
    computeFieldNumbersForContext({
      fieldTree,
      parentQuestionNumber,
      isFieldVisible: (field) => evalCondition(field, { rowData: rowContext }),
      skipFieldIds: categoryFieldIds,
    });

  const selectedCategories = useMemo(() => {
    if (!isCategorized) return ["default"];
    const allCats = new Set();
    categoryFields.forEach((cf) => {
      const vals = answers[`${tableId}_${cf.fieldId}`] || [];
      vals.forEach((v) => allCats.add(v));
    });
    return Array.from(allCats);
  }, [categoryFields, answers, tableId, isCategorized]);

  useEffect(() => {
    if (!definition || loading) return;
    setAnswers((prev) => {
      const tableData = prev[tableId] || {};
      let changed = false;
      const newTableData = { ...tableData };
      selectedCategories.forEach((catKey) => {
        if (!newTableData[catKey] || newTableData[catKey].length === 0) {
          newTableData[catKey] = [{ rowId: "row_1", values: {} }];
          changed = true;
        }
      });
      if (changed) return { ...prev, [tableId]: newTableData };
      return prev;
    });
  }, [
    tableId,
    selectedCategories.join(","),
    definition,
    loading,
    setAnswers,
    answers[tableId],
  ]);

  const collectTableDescendantFieldIds = (
    parentFieldId,
    collected = new Set(),
  ) => {
    fields.forEach((field) => {
      if (
        field.displayCondition?.some(
          (condition) =>
            (condition.triggerQuestionId || condition.triggerFieldId) ===
            parentFieldId,
        )
      ) {
        if (!collected.has(field.fieldId)) {
          collected.add(field.fieldId);
          collectTableDescendantFieldIds(field.fieldId, collected);
        }
      }
    });

    return collected;
  };

  const collectVisibleTableFieldKeys = (
    parentFieldId,
    rowValues,
    visibleKeys = new Set(),
  ) => {
    const visibleChildren = fields.filter((field) => {
      if (!field.displayCondition) return false;

      const parentConditions = field.displayCondition.filter(
        (condition) =>
          (condition.triggerQuestionId || condition.triggerFieldId) ===
          parentFieldId,
      );

      if (parentConditions.length === 0) return false;

      const logicalOp = (field.logicalOperator || "AND").toUpperCase();
      const isSatisfied = (condition) =>
        evalCondition(condition, {
          rowData: rowValues,
        });

      if (logicalOp === "OR") {
        return parentConditions.some(isSatisfied);
      }

      return parentConditions.some(isSatisfied);
    });

    visibleChildren.forEach((field) => {
      visibleKeys.add(field.fieldId);

      Object.keys(rowValues).forEach((rowKey) => {
        if (belongsToField(rowKey, field.fieldId)) {
          visibleKeys.add(rowKey);
        }
      });

      collectVisibleTableFieldKeys(field.fieldId, rowValues, visibleKeys);
    });

    return visibleKeys;
  };

  const clearHiddenTableDescendantValues = (parentFieldId, rowValues) => {
    const descendantFieldIds = collectTableDescendantFieldIds(parentFieldId);
    if (descendantFieldIds.size === 0) return rowValues;

    const visibleKeys = collectVisibleTableFieldKeys(
      parentFieldId,
      rowValues,
      new Set(),
    );

    const nextRowValues = { ...rowValues };

    Object.keys(nextRowValues).forEach((rowKey) => {
      const belongsToDescendant = Array.from(descendantFieldIds).some(
        (fieldId) => belongsToField(rowKey, fieldId),
      );

      if (!belongsToDescendant) return;

      const stillVisible = Array.from(visibleKeys).some((visibleKey) =>
        belongsToField(rowKey, visibleKey),
      );

      if (!stillVisible) {
        delete nextRowValues[rowKey];
      }
    });

    return nextRowValues;
  };

  const setTableRow = (catKey, rowId, rowData) => {
    if (readOnly) return;

    let updatedRowData = { ...rowData };
    const tableData = answers[tableId] || {};
    const groupRows = tableData[catKey] || [];
    const existingRow = groupRows.find((r) => r.rowId === rowId);

    const changedFieldIds = fields
      .filter((field) => {
        // Skip category fields as they are not stored in row.values
        if (categoryFieldIds.has(field.fieldId)) return false;

        const fieldId = field.fieldId;
        const previousEntries = Object.entries(
          existingRow?.values || {},
        ).filter(([key]) => belongsToField(key, fieldId));
        const nextEntries = Object.entries(updatedRowData).filter(([key]) =>
          belongsToField(key, fieldId),
        );

        return JSON.stringify(previousEntries) !== JSON.stringify(nextEntries);
      })
      .map((field) => field.fieldId);

    // Validation: Waste sent for recycling cannot exceed Total recyclable waste
    if (
      updatedRowData.recyclable_waste !== undefined &&
      updatedRowData.waste_sent_recycling !== undefined
    ) {
      const total = parseFloat(updatedRowData.recyclable_waste);
      const sent = parseFloat(updatedRowData.waste_sent_recycling);
      if (!isNaN(total) && !isNaN(sent) && sent > total) {
        setPopupData({
          type: "error",
          title: "Validation Error",
          message:
            "Waste sent for recycling cannot exceed Total recyclable waste!",
        });
        setShowPopup(true);
        const changedFieldKey = changedFieldIds[0];
        if (changedFieldKey) {
          updatedRowData[changedFieldKey] =
            existingRow?.values?.[changedFieldKey] !== undefined
              ? existingRow.values[changedFieldKey]
              : "";
        }
      }
    }

    // Validation for looped keys
    const loopKeys = Object.keys(updatedRowData).filter((k) =>
      k.startsWith("recyclable_waste_loop_"),
    );
    for (const key of loopKeys) {
      const suffix = key.replace("recyclable_waste_loop_", "");
      const sentKey = `waste_sent_recycling_loop_${suffix}`;
      if (updatedRowData[sentKey] !== undefined) {
        const total = parseFloat(updatedRowData[key]);
        const sent = parseFloat(updatedRowData[sentKey]);
        if (!isNaN(total) && !isNaN(sent) && sent > total) {
          setPopupData({
            type: "error",
            title: "Validation Error",
            message:
              "Waste sent for recycling cannot exceed Total recyclable waste!",
          });
          setShowPopup(true);
          const changedFieldKey = changedFieldIds[0];
          if (changedFieldKey) {
            updatedRowData[changedFieldKey] =
              existingRow?.values?.[changedFieldKey] !== undefined
                ? existingRow.values[changedFieldKey]
                : "";
          }
        }
      }
    }

    // --- helpers ---
    const parseLoopKey = (key) => {
      const match = key.match(/_loop_(\d+)/); // ✅ matches "_loop_1" whether it's at the end
      return match ? { loopIndex: Number(match[1]) } : null; // or followed by "_co2" etc.
    };

    // Build a context where loop-suffixed values are exposed under their base fieldId,
    // same as the render logic's `loopValues`, so evalCondition works correctly.
    const getLoopContext = (loopIndex, rowValues) => {
      const ctx = { ...rowValues };
      fields.forEach((f) => {
        const loopKey = `${f.fieldId}_loop_${loopIndex}`;
        if (rowValues[loopKey] !== undefined) {
          ctx[f.fieldId] = rowValues[loopKey];
        }
      });
      return ctx;
    };

    // Loop-scoped version of clearHiddenTableDescendantValues:
    // only ever touches keys suffixed with the SAME loopIndex.
    const clearHiddenLoopDescendantValues = (
      parentFieldId,
      loopIndex,
      rowValues,
    ) => {
      const descendantFieldIds = collectTableDescendantFieldIds(parentFieldId);
      if (descendantFieldIds.size === 0) return rowValues;

      const loopCtx = getLoopContext(loopIndex, rowValues);
      const visibleKeys = collectVisibleTableFieldKeys(
        parentFieldId,
        loopCtx,
        new Set(),
      );

      const nextRowValues = { ...rowValues };
      const suffix = `_loop_${loopIndex}`;

      Object.keys(nextRowValues).forEach((rowKey) => {
        // Only ever consider keys that belong to THIS loop instance
        if (!rowKey.includes(suffix)) return;

        const belongsToDescendant = Array.from(descendantFieldIds).some(
          (fieldId) =>
            rowKey === `${fieldId}${suffix}` ||
            rowKey.startsWith(`${fieldId}${suffix}_`),
        );
        if (!belongsToDescendant) return;

        const stillVisible = Array.from(visibleKeys).some(
          (visibleKey) =>
            rowKey === `${visibleKey}${suffix}` ||
            rowKey.startsWith(`${visibleKey}${suffix}_`),
        );

        if (!stillVisible) {
          delete nextRowValues[rowKey];
        }
      });

      return nextRowValues;
    };

    changedFieldIds.forEach((fieldId) => {
      // Figure out every raw key for this field that actually changed, so we know which loop indices to touch
      const relevantKeys = Object.keys(updatedRowData).filter((k) =>
        belongsToField(k, fieldId),
      );
      const loopIndices = new Set(
        relevantKeys
          .map((k) => parseLoopKey(k)?.loopIndex)
          .filter((v) => v !== undefined),
      );
      if (loopIndices.size > 0) {
        // Loop-aware clearing — never crosses into other entries
        loopIndices.forEach((idx) => {
          updatedRowData = clearHiddenLoopDescendantValues(
            fieldId,
            idx,
            updatedRowData,
          );
        });
      } else {
        // Non-repeater field — existing behavior is fine
        updatedRowData = clearHiddenTableDescendantValues(
          fieldId,
          updatedRowData,
        );
      }

      // If a multiselect field changed, purge any row-value keys suffixed with deselected option values
      const changedField = fields.find((f) => f.fieldId === fieldId);
      if (
        changedField &&
        ["multiselect", "multi-select"].includes(changedField.fieldType)
      ) {
        const prevValues = Array.isArray(existingRow?.values?.[fieldId])
          ? existingRow.values[fieldId]
          : [];
        const nextValues = Array.isArray(updatedRowData[fieldId])
          ? updatedRowData[fieldId]
          : [];
        const deselectedOptions = prevValues.filter(
          (v) => !nextValues.includes(v),
        );

        if (deselectedOptions.length > 0) {
          Object.keys(updatedRowData).forEach((key) => {
            const isOrphanedSuffix = deselectedOptions.some(
              (opt) =>
                key.endsWith(`_${opt}`) ||
                key.includes(`_${opt}_`) ||
                (key.includes(`_loop_`) && key.includes(`_${opt}`)),
            );
            if (isOrphanedSuffix) {
              delete updatedRowData[key];
            }
          });
        }
      }
    });

    // ... (Your existing calculation logic remains here)
    const calculationGroups = {};

    fields.forEach((field) => {
      // Check if this field has resultAction (e.g., "avg", "total")
      if (field.resultAction) {
        const action = field.resultAction;

        // Find source fields based on calculationType
        const sourceFields = fields.filter(
          (f) =>
            f.calculationType &&
            Array.isArray(f.calculationType) &&
            f.calculationType.includes(action),
        );

        if (sourceFields.length > 0) {
          if (!calculationGroups[action]) {
            calculationGroups[action] = [];
          }
          calculationGroups[action].push({
            targetField: field.fieldId,
            sourceFields: sourceFields.map((f) => f.fieldId),
            action: action,
          });
        }
      }
    });

    // Execute calculations
    Object.values(calculationGroups).forEach((calculations) => {
      calculations.forEach(({ targetField, sourceFields, action }) => {
        let total = 0;
        let count = 0;
        let hasData = false;

        sourceFields.forEach((sourceId) => {
          const value = updatedRowData[sourceId];
          if (value !== undefined && value !== null && value !== "") {
            const numValue = parseFloat(value) || 0;
            total += numValue;
            count++;
            hasData = true;
          }
        });

        if (hasData) {
          if (action === "total") {
            updatedRowData[targetField] = total;
          } else if (action === "avg") {
            updatedRowData[targetField] =
              count > 0 ? Number((total / count).toFixed(4)) : 0;
          }
        } else {
          // Clear the calculated field if no source data
          updatedRowData[targetField] = "";
        }
      });
    });

    setAnswers((prev) => {
      const nextTableData = prev[tableId] || {};
      const nextGroupRows = nextTableData[catKey] || [];
      const idx = nextGroupRows.findIndex((r) => r.rowId === rowId);
      const updated = [...nextGroupRows];
      if (idx >= 0) updated[idx] = { ...updated[idx], values: updatedRowData };
      else updated.push({ rowId, values: updatedRowData });
      return { ...prev, [tableId]: { ...nextTableData, [catKey]: updated } };
    });
  };

  const getMultiselectDependency = (field, rowData) => {
    // 1. Find ALL multiselect dependencies that use "exists"
    const multiselectDeps = field.displayCondition?.filter((cond) => {
      if (cond.operator !== "exists") return false;

      // FIX: If the table is categorized, ignore the category field as a trigger
      // (it's handled by the outer category loop, so we don't want a nested loop here)
      if (
        isCategorized &&
        categoryFields.some((cf) => cf.fieldId === cond.triggerQuestionId)
      ) {
        return false;
      }

      const triggerField = fields.find(
        (f) => f.fieldId === cond.triggerQuestionId,
      );
      return triggerField?.fieldType === "multiselect";
    });

    // 2. Aggregate selected items from ALL dependent multiselects
    const aggregatedFields = [];
    const allSelectedItems = [];

    if (multiselectDeps) {
      multiselectDeps.forEach((dep) => {
        const triggerField = fields.find(
          (f) => f.fieldId === dep.triggerQuestionId,
        );
        const selected = rowData[dep.triggerQuestionId] || [];

        if (Array.isArray(selected) && selected.length > 0) {
          aggregatedFields.push({
            field: triggerField,
            selectedItems: selected,
          });
          allSelectedItems.push(...selected);
        }
      });
    }

    if (aggregatedFields.length > 0) {
      return {
        fields: aggregatedFields,
        allSelectedItems: [...new Set(allSelectedItems)],
      };
    }
    return null;
  };

  if (loading)
    return (
      <div className="p-4 bg-gray-50 border rounded animate-pulse">
        Loading Table...
      </div>
    );
  if (!definition) return null;

  return (
    <div className="space-y-6">
      {isCategorized &&
        categoryFields.map((cf) => (
          <div
            key={cf.fieldId}
            className="mb-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded"
          >
            {cf.guidance && cf.guidance.length > 0 && (
              <div className="mb-2 p-2 bg-blue-50 rounded border-l-2 border-blue-400">
                <p className="text-xs font-semibold text-blue-800 mb-1">
                  💡 Guidance:
                </p>
                <ul className="text-xs text-gray-600 space-y-0.5 list-disc list-inside">
                  {cf.guidance.map((item, idx) => (
                    <li key={idx} className="leading-relaxed">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {cf.fieldTitle && cf.fieldTitle.length > 0 && (
              <p className="block text-sm font-semibold text-gray-800 mb-2 mt-4 border-b  pb-1">
                {cf.fieldTitle[0]}
              </p>
            )}
            <label className="block font-bold mb-2">
              {cf.fieldLabel}
              <RequiredMark show={cf.mandatory || parentMandatory} />
            </label>
            <FormQuestionInput
              field={cf}
              value={answers[`${tableId}_${cf.fieldId}`] || []}
              disabled={readOnly}
              onChange={(val) => {
                if (readOnly) return;
                setAnswers((prev) => {
                  const prevCatValues = prev[`${tableId}_${cf.fieldId}`] || [];
                  const nextCatValues = Array.isArray(val) ? val : [];
                  const deselected = prevCatValues.filter(
                    (v) => !nextCatValues.includes(v),
                  );

                  const nextState = {
                    ...prev,
                    [`${tableId}_${cf.fieldId}`]: nextCatValues,
                  };

                  // Remove the table data bucket for each deselected category
                  if (deselected.length > 0) {
                    const prevTableData = prev[tableId] || {};
                    const nextTableData = { ...prevTableData };
                    deselected.forEach((opt) => {
                      delete nextTableData[opt];
                    });
                    nextState[tableId] = nextTableData;
                  }

                  return nextState;
                });
              }}
            />
          </div>
        ))}
      {selectedCategories.map((catKey) => {
        const tableData = answers[tableId] || {};
        const rows = tableData[catKey] || [];

        let catLabel = catKey;
        for (const cf of categoryFields) {
          const opt = cf.options?.find((o) => o.value === catKey);
          if (opt) {
            catLabel = opt.label;
            break;
          }
        }

        return (
          <div
            key={catKey}
            className="border-2 border-gray-200 rounded-xl p-4 bg-white"
          >
            {isCategorized && (
              <h3 className="text-lg font-bold text-teal-700 mb-4 uppercase">
                {catLabel}
              </h3>
            )}
            {rows.map((row) => {
              const context = { ...row.values };
              if (isCategorized) {
                const activeCategoryFields = categoryFields.filter((cf) => {
                  const vals = answers[`${tableId}_${cf.fieldId}`] || [];
                  return vals.includes(catKey);
                });
                categoryFields.forEach((cf) => {
                  if (activeCategoryFields.includes(cf)) {
                    context[cf.fieldId] = [catKey];
                  } else {
                    context[cf.fieldId] = [];
                  }
                });
              }
              const fieldNumbers = getFieldNumbersForRow(context);
              return (
                <div
                  key={row.rowId}
                  className="mb-6 p-4 border rounded-lg bg-gray-50 relative"
                >
                  <div className="space-y-4">
                    {(() => {
                      const renderedFieldIds = new Set();
                      return fields.map((field, idx) => {
                        if (
                          isCategorized &&
                          categoryFields.find(
                            (cf) => cf.fieldId === field.fieldId,
                          )
                        )
                          return null;
                        if (renderedFieldIds.has(field.fieldId)) return null;
                        if (!evalCondition(field, { rowData: context }))
                          return null;
                        const isRepeater =
                          field.fieldType === "number" &&
                          parseInt(row.values[field.fieldId]) > 0 &&
                          fields.some((other) =>
                            other.displayCondition?.some(
                              (c) =>
                                c.triggerQuestionId === field.fieldId ||
                                c.triggerFieldId === field.fieldId,
                            ),
                          );

                        if (isRepeater) {
                          const repeaterCount = parseInt(
                            row.values[field.fieldId],
                          );

                          // **RECURSIVE DEPENDENCY SEARCH**
                          // Find all fields that belong to this repeater loop, either directly or indirectly.
                          // 1. Direct dependencies: Fields triggering off 'number_of_items'
                          // 2. Verified Loop Members: Fields triggering off known loop members
                          const loopMemberIds = new Set();
                          let previousSize = 0;

                          // Pass 1: Direct Dependencies
                          fields.forEach((f) => {
                            const dependsOnRoot = f.displayCondition?.some(
                              (c) =>
                                c.triggerQuestionId === field.fieldId ||
                                c.triggerFieldId === field.fieldId,
                            );
                            if (dependsOnRoot) loopMemberIds.add(f.fieldId);
                          });

                          // Pass 2+: Transitive Dependencies
                          // Keep adding fields that depend on current loop members until no new fields are added
                          while (loopMemberIds.size > previousSize) {
                            previousSize = loopMemberIds.size;
                            fields.forEach((f) => {
                              if (!loopMemberIds.has(f.fieldId)) {
                                const dependsOnMember =
                                  f.displayCondition?.some((c) => {
                                    const tId =
                                      c.triggerQuestionId || c.triggerFieldId;
                                    return loopMemberIds.has(tId);
                                  });
                                if (dependsOnMember)
                                  loopMemberIds.add(f.fieldId);
                              }
                            });
                          }

                          const childFields = fields.filter((f) =>
                            loopMemberIds.has(f.fieldId),
                          );

                          renderedFieldIds.add(field.fieldId);
                          childFields.forEach((cf) =>
                            renderedFieldIds.add(cf.fieldId),
                          );

                          return (
                            <div key={field.fieldId} className="space-y-4">
                              <div key={field.fieldId}>
                                {field.fieldTitle &&
                                  field.fieldTitle.length > 0 && (
                                    <p className="block text-sm font-semibold text-gray-800 mb-2 mt-4 border-b  pb-1">
                                      {field.fieldTitle[0]}
                                    </p>
                                  )}
                                {field.guidance &&
                                  field.guidance.length > 0 && (
                                    <div className="mb-2 p-2 bg-blue-50 rounded border-l-2 border-blue-400">
                                      <p className="text-xs font-semibold text-blue-800 mb-1">
                                        💡 Guidance:
                                      </p>
                                      <ul className="text-xs text-gray-600 space-y-0.5 list-disc list-inside">
                                        {field.guidance.map((item, idx) => (
                                          <li
                                            key={idx}
                                            className="leading-relaxed"
                                          >
                                            {item}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                <label className="block text-sm font-medium mb-1">
                                  <FieldSerialPrefix
                                    fieldNumbers={fieldNumbers}
                                    fieldId={field.fieldId}
                                  />
                                  {field.fieldLabel}
                                  <RequiredMark
                                    show={field.mandatory || parentMandatory}
                                  />
                                </label>
                                <FormQuestionInput
                                  field={field}
                                  value={row.values[field.fieldId]}
                                  disabled={
                                    readOnly ||
                                    [
                                      "total_annual_consumption",
                                      "avg_annual_consumption",
                                    ].includes(field.fieldId)
                                  }
                                  onChange={(val) =>
                                    setTableRow(catKey, row.rowId, {
                                      ...row.values,
                                      [field.fieldId]: val,
                                    })
                                  }
                                />
                              </div>
                              <div className="mt-4 space-y-6">
                                {(() => {
                                  const entryLabel =
                                    field.fieldId.includes("vehicle") ||
                                    field.fieldLabel
                                      .toLowerCase()
                                      .includes("vehicle")
                                      ? "Vehicle"
                                      : "Entry";
                                  return Array.from(
                                    { length: repeaterCount },
                                    (_, i) => (
                                      <div
                                        key={`${row.rowId}_${field.fieldId}_loop_${i}`}
                                        className="p-4 border-2 border-teal-100 rounded-xl bg-teal-50/10"
                                      >
                                        <div className="text-teal-600 font-bold mb-4 uppercase text-sm tracking-wider">
                                          {entryLabel} {i + 1}
                                        </div>
                                        {childFields.map((cf) => {
                                          const valueKey = `${cf.fieldId}_loop_${i}`;

                                          // Create Scoped Context: Map all child fields' loop values to their base IDs
                                          // This allows siblings in the loop to see each other's values
                                          const loopValues = { ...context }; // Start with row values
                                          childFields.forEach((sibling) => {
                                            const sKey = `${sibling.fieldId}_loop_${i}`;
                                            if (
                                              row.values[sKey] !== undefined
                                            ) {
                                              loopValues[sibling.fieldId] =
                                                row.values[sKey];
                                            }
                                          });

                                          // Check visibility for child within the loop context
                                          if (
                                            !evalCondition(cf, {
                                              rowData: {
                                                ...loopValues,
                                                repeatingParentId:
                                                  field.fieldId,
                                              },
                                            })
                                          )
                                            return null;

                                          // **LOOP-AWARE MULTISELECT CHECK**
                                          // Check if this child depends on a multiselect *within this loop*
                                          const loopMsDep =
                                            getMultiselectDependency(
                                              cf,
                                              loopValues,
                                              null,
                                              null,
                                              fields,
                                            );

                                          if (loopMsDep) {
                                            const {
                                              fields: msFields,
                                              allSelectedItems,
                                            } = loopMsDep;

                                            return (
                                              <div
                                                key={valueKey}
                                                className="mb-4  border-l-2 border-teal-50"
                                              >
                                                {/* <label className="block text-sm font-semibold text-gray-800 mb-2">
                                                                                                {cf.fieldLabel}
                                                                                            </label> */}
                                                <div className="rounded-lg  shadow-sm ">
                                                  {allSelectedItems.map(
                                                    (itemValue) => {
                                                      // Resolve label
                                                      const sourceMs =
                                                        msFields.find((mf) =>
                                                          mf.selectedItems.includes(
                                                            itemValue,
                                                          ),
                                                        );
                                                      const opt =
                                                        sourceMs?.field.options?.find(
                                                          (o) =>
                                                            o.value ===
                                                            itemValue,
                                                        );
                                                      const label =
                                                        opt?.label || itemValue;

                                                      // Value key includes loop index AND option suffix
                                                      const splitValueKey = `${cf.fieldId}_loop_${i}_${itemValue}`;

                                                      return (
                                                        <div
                                                          key={itemValue}
                                                          className="w-full"
                                                        >
                                                          <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            <FieldSerialPrefix
                                                              fieldNumbers={
                                                                fieldNumbers
                                                              }
                                                              fieldId={
                                                                cf.fieldId
                                                              }
                                                            />
                                                            {cf.fieldLabel}
                                                            {label}
                                                            <RequiredMark
                                                              show={
                                                                cf.mandatory ||
                                                                parentMandatory
                                                              }
                                                            />
                                                          </label>
                                                          <div className="flex items-center gap-2">
                                                            <div className="flex-grow">
                                                              <FormQuestionInput
                                                                field={cf}
                                                                value={
                                                                  row.values[
                                                                    splitValueKey
                                                                  ]
                                                                }
                                                                disabled={
                                                                  readOnly
                                                                }
                                                                onChange={(
                                                                  val,
                                                                ) =>
                                                                  setTableRow(
                                                                    catKey,
                                                                    row.rowId,
                                                                    {
                                                                      ...row.values,
                                                                      [splitValueKey]:
                                                                        val,
                                                                    },
                                                                  )
                                                                }
                                                              />
                                                            </div>
                                                          </div>
                                                        </div>
                                                      );
                                                    },
                                                  )}
                                                </div>
                                                {/* Mini Validation for Loop Group */}
                                                {(cf.fieldLabel
                                                  .toLowerCase()
                                                  .includes("percentage") ||
                                                  cf.fieldType ===
                                                    "percentage") && (
                                                  <div className="mt-2 text-right">
                                                    {(() => {
                                                      const total =
                                                        allSelectedItems.reduce(
                                                          (sum, item) =>
                                                            sum +
                                                            (parseFloat(
                                                              row.values[
                                                                `${cf.fieldId}_loop_${i}_${item}`
                                                              ],
                                                            ) || 0),
                                                          0,
                                                        );
                                                      const isCorrect =
                                                        total === 100;
                                                      const isVisible =
                                                        total !== 0;

                                                      // return total !== 0 ? (
                                                      //     <span className={`text-xs font-bold ${isCorrect ? "text-green-600" : "text-red-500"}`}>
                                                      //         Total: {total.toFixed(1)}% {isCorrect ? "✅" : "⚠️ (Must be 100%)"}
                                                      //     </span>
                                                      // ) : null;
                                                      return (
                                                        <div
                                                          key={cf.fieldId}
                                                          className="flex justify-between items-center text-sm font-bold p-3 bg-white border border-gray-200 rounded-lg shadow-sm w-full"
                                                        >
                                                          {/* <div className="flex flex-col"> */}
                                                          <span>
                                                            Total Percentage:
                                                          </span>
                                                          {isVisible &&
                                                            !isCorrect && (
                                                              <span className="text-xs text-red-500 font-bold animate-pulse">
                                                                ⚠️ Total must be
                                                                100%
                                                              </span>
                                                            )}
                                                          {/* </div> */}
                                                          <span
                                                            className={`text-lg transition-all duration-300 ${
                                                              isCorrect
                                                                ? "text-green-600 scale-110"
                                                                : "text-red-600"
                                                            }`}
                                                          >
                                                            {total.toFixed(1)}%
                                                          </span>
                                                        </div>
                                                      );
                                                    })()}
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          }

                                          return (
                                            <div
                                              key={valueKey}
                                              className="mb-3"
                                            >
                                              {cf.guidance &&
                                                cf.guidance.length > 0 && (
                                                  <div className="mb-2 p-2 bg-blue-50 rounded border-l-2 border-blue-400">
                                                    <p className="text-xs font-semibold text-blue-800 mb-1">
                                                      💡 Guidance:
                                                    </p>
                                                    <ul className="text-xs text-gray-600 space-y-0.5 list-disc list-inside">
                                                      {cf.guidance.map(
                                                        (item, idx) => (
                                                          <li
                                                            key={idx}
                                                            className="leading-relaxed"
                                                          >
                                                            {item}
                                                          </li>
                                                        ),
                                                      )}
                                                    </ul>
                                                  </div>
                                                )}
                                              <label className="block text-sm font-medium mb-1">
                                                {cf.fieldTitle}
                                              </label>

                                              <label className="block text-sm font-medium mb-1">
                                                <FieldSerialPrefix
                                                  fieldNumbers={fieldNumbers}
                                                  fieldId={cf.fieldId}
                                                />
                                                {cf.fieldLabel}
                                                <RequiredMark
                                                  show={
                                                    cf.mandatory ||
                                                    parentMandatory
                                                  }
                                                />
                                              </label>
                                              <div>
                                                <FormQuestionInput
                                                  field={cf}
                                                  value={row.values[valueKey]}
                                                  disabled={readOnly}
                                                  onChange={(val) =>
                                                    setTableRow(
                                                      catKey,
                                                      row.rowId,
                                                      {
                                                        ...row.values,
                                                        [valueKey]: val,
                                                      },
                                                    )
                                                  }
                                                />
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ),
                                  );
                                })()}
                              </div>
                            </div>
                          );
                        }
                        // Determine if this and next can be paired
                        const isNumOrPerc = (f) =>
                          f?.fieldType === "number" ||
                          f?.fieldType === "percentage";
                        const isInput = (f) =>
                          ["number", "percentage", "select", "text"].includes(
                            f?.fieldType,
                          );
                        const nextField = fields[idx + 1];

                        // Double pairing: Input + File
                        const canPairInputWithFile =
                          isInput(field) &&
                          !getMultiselectDependency(field, row.values) &&
                          nextField &&
                          nextField.fieldType === "file" &&
                          !getMultiselectDependency(nextField, row.values) &&
                          !renderedFieldIds.has(nextField.fieldId) &&
                          evalCondition(nextField, { rowData: context });

                        if (canPairInputWithFile) {
                          renderedFieldIds.add(field.fieldId);
                          renderedFieldIds.add(nextField.fieldId);

                          return (
                            <div
                              key={`pair_file_container_${field.fieldId}`}
                              className="mt-6 border-t pt-4 first:border-0 first:pt-0"
                            >
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                  <label className="block text-sm font-medium mb-1">
                                    <FieldSerialPrefix
                                      fieldNumbers={fieldNumbers}
                                      fieldId={field.fieldId}
                                    />
                                    {field.fieldLabel}
                                    <RequiredMark
                                      show={field.mandatory || parentMandatory}
                                    />
                                  </label>
                                  <FormQuestionInput
                                    field={field}
                                    value={row.values[field.fieldId]}
                                    disabled={readOnly}
                                    onChange={(val) =>
                                      setTableRow(catKey, row.rowId, {
                                        ...row.values,
                                        [field.fieldId]: val,
                                      })
                                    }
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium mb-1">
                                    <FieldSerialPrefix
                                      fieldNumbers={fieldNumbers}
                                      fieldId={nextField.fieldId}
                                    />
                                    {nextField.fieldLabel}
                                    <RequiredMark
                                      show={
                                        nextField.mandatory || parentMandatory
                                      }
                                    />
                                  </label>
                                  <FormQuestionInput
                                    field={nextField}
                                    value={row.values[nextField.fieldId]}
                                    disabled={readOnly}
                                    onChange={(val) =>
                                      setTableRow(catKey, row.rowId, {
                                        ...row.values,
                                        [nextField.fieldId]: val,
                                      })
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        }

                        const canPairWithNext =
                          isNumOrPerc(field) &&
                          !getMultiselectDependency(field, row.values) &&
                          nextField &&
                          isNumOrPerc(nextField) &&
                          !getMultiselectDependency(nextField, row.values) &&
                          !renderedFieldIds.has(nextField.fieldId) &&
                          evalCondition(nextField, { rowData: context });

                        if (canPairWithNext) {
                          renderedFieldIds.add(field.fieldId);
                          renderedFieldIds.add(nextField.fieldId);

                          const hasTitles =
                            (field.fieldTitle && field.fieldTitle.length > 0) ||
                            (nextField.fieldTitle &&
                              nextField.fieldTitle.length > 0);
                          const hasGuidance =
                            (field.guidance && field.guidance.length > 0) ||
                            (nextField.guidance &&
                              nextField.guidance.length > 0);

                          return (
                            <div
                              key={`pair_container_${field.fieldId}`}
                              className="mt-6 border-t pt-4 first:border-0 first:pt-0"
                            >
                              {/* Titles Row */}
                              {hasTitles && (
                                <div className="mb-2">
                                  {field.fieldTitle?.[0] &&
                                  nextField.fieldTitle?.[0] ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                      <p className="text-sm font-semibold text-gray-800 border-b pb-1">
                                        {field.fieldTitle[0]}
                                      </p>
                                      <p className="text-sm font-semibold text-gray-800 border-b pb-1">
                                        {nextField.fieldTitle[0]}
                                      </p>
                                    </div>
                                  ) : (
                                    <p className="text-sm font-semibold text-gray-800 border-b pb-1">
                                      {field.fieldTitle?.[0] ||
                                        nextField.fieldTitle?.[0]}
                                    </p>
                                  )}
                                </div>
                              )}

                              {/* Guidance Row */}
                              {hasGuidance && (
                                <div className="mb-3">
                                  {field.guidance?.length > 0 &&
                                  nextField.guidance?.length > 0 ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                      <div className="p-2 bg-blue-50 rounded border-l-2 border-blue-400">
                                        <p className="text-xs font-semibold text-blue-800 mb-1">
                                          💡 Guidance:
                                        </p>
                                        <ul className="text-xs text-gray-600 space-y-0.5 list-disc list-inside">
                                          {field.guidance.map((item, idx) => (
                                            <li
                                              key={idx}
                                              className="leading-relaxed"
                                            >
                                              {item}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                      <div className="p-2 bg-blue-50 rounded border-l-2 border-blue-400">
                                        <p className="text-xs font-semibold text-blue-800 mb-1">
                                          💡 Guidance:
                                        </p>
                                        <ul className="text-xs text-gray-600 space-y-0.5 list-disc list-inside">
                                          {nextField.guidance.map(
                                            (item, idx) => (
                                              <li
                                                key={idx}
                                                className="leading-relaxed"
                                              >
                                                {item}
                                              </li>
                                            ),
                                          )}
                                        </ul>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="p-2 bg-blue-50 rounded border-l-2 border-blue-400">
                                      <p className="text-xs font-semibold text-blue-800 mb-1">
                                        💡 Guidance:
                                      </p>
                                      <ul className="text-xs text-gray-600 space-y-0.5 list-disc list-inside">
                                        {(field.guidance?.length > 0
                                          ? field.guidance
                                          : nextField.guidance
                                        ).map((item, idx) => (
                                          <li
                                            key={idx}
                                            className="leading-relaxed"
                                          >
                                            {item}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Inputs Row */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                  <label className="block text-sm font-medium mb-1">
                                    <FieldSerialPrefix
                                      fieldNumbers={fieldNumbers}
                                      fieldId={field.fieldId}
                                    />
                                    {field.fieldLabel}
                                    <RequiredMark
                                      show={field.mandatory || parentMandatory}
                                    />
                                  </label>
                                  <FormQuestionInput
                                    field={field}
                                    value={row.values[field.fieldId]}
                                    disabled={
                                      readOnly ||
                                      [
                                        "total_annual_consumption",
                                        "avg_annual_consumption",
                                      ].includes(field.fieldId)
                                    }
                                    onChange={(val) =>
                                      setTableRow(catKey, row.rowId, {
                                        ...row.values,
                                        [field.fieldId]: val,
                                      })
                                    }
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium mb-1">
                                    <FieldSerialPrefix
                                      fieldNumbers={fieldNumbers}
                                      fieldId={nextField.fieldId}
                                    />
                                    {nextField.fieldLabel}
                                    <RequiredMark
                                      show={
                                        nextField.mandatory || parentMandatory
                                      }
                                    />
                                  </label>
                                  <FormQuestionInput
                                    field={nextField}
                                    value={row.values[nextField.fieldId]}
                                    disabled={
                                      readOnly ||
                                      [
                                        "total_annual_consumption",
                                        "avg_annual_consumption",
                                      ].includes(nextField.fieldId)
                                    }
                                    onChange={(val) =>
                                      setTableRow(catKey, row.rowId, {
                                        ...row.values,
                                        [nextField.fieldId]: val,
                                      })
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        }

                        // Fallback for non-numeric or single fields
                        renderedFieldIds.add(field.fieldId);

                        const multiselectDep = getMultiselectDependency(
                          field,
                          row.values,
                        );

                        if (multiselectDep) {
                          // 1. IDENTIFY THE GROUP: Find other questions that share the same multiselect dependencies
                          const relatedQuestions = fields.filter((q) => {
                            // Ensure we only group questions that are meant to be 'visible'
                            if (!evalCondition(q, { rowData: context }))
                              return false;

                            // Calculate dependency for the candidate question
                            const qDep = getMultiselectDependency(
                              q,
                              row.values,
                            );
                            if (!qDep) return false;

                            // Compare the triggering multiselect fields
                            // This ensures we group questions driven by the same user selections
                            const currentTriggerIds = multiselectDep.fields
                              .map((f) => f.field.fieldId)
                              .sort()
                              .join(",");
                            const qTriggerIds = qDep.fields
                              .map((f) => f.field.fieldId)
                              .sort()
                              .join(",");

                            return currentTriggerIds === qTriggerIds;
                          });

                          // 2. CHECK IF LEADER: Are we the first question in this group?
                          // Use fieldId for comparison as questionId might not be unique in this context
                          const isLeader =
                            relatedQuestions.length > 0 &&
                            relatedQuestions[0].fieldId === field.fieldId;

                          // 3. IF NOT LEADER: Skip rendering (it will be rendered by the leader)
                          if (!isLeader) {
                            return null;
                          }

                          // 4. LEADER LOGIC: Render the Master Table
                          const {
                            fields: multiselectFields,
                            allSelectedItems,
                          } = multiselectDep;

                          // Mark all related questions as rendered so they don't render individually later
                          relatedQuestions.forEach((q) =>
                            renderedFieldIds.add(q.fieldId),
                          );

                          return (
                            <div
                              key={`group_${field.fieldId}`}
                              className="mb-4 pl-4 border-l-2 border-teal-50"
                            >
                              <div className="rounded-lg border border-gray-200 shadow-sm bg-white divide-y divide-gray-200">
                                {/* Header Row: Column Titles (Optional, but good for context if fields align) */}
                                {/* For vertical layout, maybe just a main header or iterate items */}

                                {allSelectedItems.map((itemValue, idx) => {
                                  // Find option metadata
                                  const sourceMultiselect =
                                    multiselectFields.find((mf) =>
                                      mf.selectedItems.includes(itemValue),
                                    );
                                  const option =
                                    sourceMultiselect?.field.options?.find(
                                      (opt) => opt.value === itemValue,
                                    );
                                  const optionLabel =
                                    option?.label || itemValue;

                                  return (
                                    <div key={itemValue} className="p-6">
                                      {/* <h4 className="text-lg font-bold text-teal-700 mb-4 flex items-center gap-2">
                                                                           
                                                                            {optionLabel}
                                                                        </h4> */}

                                      <div className="grid gap-6 pl-4 border-l-2 border-teal-50/50">
                                        {relatedQuestions.map((q) => {
                                          const valueKey = `${q.fieldId}_${itemValue}`;
                                          const currentValue =
                                            row.values[valueKey];

                                          return (
                                            <div
                                              key={q.fieldId}
                                              className="w-full"
                                            >
                                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                                <FieldSerialPrefix
                                                  fieldNumbers={fieldNumbers}
                                                  fieldId={q.fieldId}
                                                />
                                                {q.fieldLabel} {optionLabel}
                                                <RequiredMark
                                                  show={
                                                    q.mandatory ||
                                                    parentMandatory
                                                  }
                                                />
                                              </label>
                                              <div className="flex items-center gap-2">
                                                <div className="flex-grow">
                                                  <FormQuestionInput
                                                    field={q}
                                                    value={currentValue}
                                                    disabled={readOnly}
                                                    onChange={(val) => {
                                                      setTableRow(
                                                        catKey,
                                                        row.rowId,
                                                        {
                                                          ...row.values,
                                                          [valueKey]: val,
                                                        },
                                                      );
                                                    }}
                                                  />
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                })}

                                {/* Footer: Validation Summary */}
                                {relatedQuestions.some(
                                  (q) =>
                                    q.fieldLabel
                                      .toLowerCase()
                                      .includes("percentage") ||
                                    q.fieldType === "percentage",
                                ) && (
                                  <div className="bg-gray-50 p-6">
                                    {/* <h5 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 border-b border-gray-200 pb-2">
                                                                        Percentage Validation
                                                                    </h5> */}
                                    <div className="grid grid-cols-1 gap-4">
                                      {relatedQuestions.map((q) => {
                                        if (
                                          !q.fieldLabel
                                            .toLowerCase()
                                            .includes("percentage") &&
                                          q.fieldType !== "percentage"
                                        ) {
                                          return null;
                                        }

                                        // Calculate Total
                                        const total = allSelectedItems.reduce(
                                          (sum, itemValue) => {
                                            const valueKey = `${q.fieldId}_${itemValue}`;
                                            return (
                                              sum +
                                              (parseFloat(
                                                row.values[valueKey],
                                              ) || 0)
                                            );
                                          },
                                          0,
                                        );
                                        const isCorrect = total === 100;
                                        const isVisible = total !== 0;

                                        return (
                                          <div
                                            key={q.fieldId}
                                            className="flex justify-between items-center text-sm font-bold p-3 bg-white border border-gray-200 rounded-lg shadow-sm w-full"
                                          >
                                            {/* <div className="flex flex-col"> */}
                                            <span>Total Percentage:</span>

                                            {isVisible && !isCorrect && (
                                              <span className="text-xs text-red-500 font-bold animate-pulse">
                                                ⚠️ Total must be 100%
                                              </span>
                                            )}
                                            {/* 
                                                                                        {isCorrect && (
                                                                                            <span className="text-xs text-green-600 font-bold">
                                                                                                ✅ Success: 100% reached
                                                                                            </span>
                                                                                        )} */}
                                            {/* </div> */}

                                            <span
                                              className={`text-lg transition-all duration-300 ${
                                                isCorrect
                                                  ? "text-green-600 scale-110"
                                                  : "text-red-600"
                                              }`}
                                            >
                                              {total.toFixed(1)}%
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div key={field.fieldId} className="mt-4">
                            {field.fieldTitle &&
                              field.fieldTitle.length > 0 && (
                                <p className="block text-sm font-semibold text-gray-800 mb-2 mt-4 border-b  pb-1">
                                  {field.fieldTitle[0]}
                                </p>
                              )}
                            {field.guidance && field.guidance.length > 0 && (
                              <div className="mb-2 p-2 bg-blue-50 rounded border-l-2 border-blue-400">
                                <p className="text-xs font-semibold text-blue-800 mb-1">
                                  💡 Guidance:
                                </p>
                                <ul className="text-xs text-gray-600 space-y-0.5 list-disc list-inside">
                                  {field.guidance.map((item, idx) => (
                                    <li key={idx} className="leading-relaxed">
                                      {item}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            <label className="block text-sm font-medium mb-1">
                              <FieldSerialPrefix
                                fieldNumbers={fieldNumbers}
                                fieldId={field.fieldId}
                              />
                              {field.fieldLabel}
                              <RequiredMark
                                show={field.mandatory || parentMandatory}
                              />
                            </label>
                            <FormQuestionInput
                              field={field}
                              value={row.values[field.fieldId]}
                              disabled={readOnly}
                              onChange={(val) =>
                                setTableRow(catKey, row.rowId, {
                                  ...row.values,
                                  [field.fieldId]: val,
                                })
                              }
                            />
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
      {showPopup &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 99999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0,0,0,0.35)",
              backdropFilter: "blur(4px)",
              WebkitBackdropFilter: "blur(4px)",
            }}
          >
            <PopupCard
              type={popupData.type}
              title={popupData.title}
              message={popupData.message}
            />
          </div>,
          document.body,
        )}
    </div>
  );
};

const QuestionTable = ({ renderTable, children }) => {
  if (typeof renderTable === "function") {
    return renderTable();
  }

  return children || null;
};

export default QuestionTable;
